import { randomNumber } from '../../../utilities/randomNumber.util'
import { CcHpiConstants } from './ccHpi.constants'
import {
  ICcHpiCardiacArrestState,
  ICcHpiAction,
  IHistoryState,
  ISituationState,
  ICcHpiInjuryDetailsState,
  IOtherComplaint,
} from './ccHpi.types'

export const otherComplaintInit = (): IOtherComplaint => {
  return {
    key: randomNumber().toString(),
    otherComplaint: '',
    ocDuration: '',
    ocDurationUnit: null,
  }
}

export const initialSituationState: ISituationState = {
  segment: 'CCH',
  complaint: '',
  patientActivity: [],
  possibleInjury: null,
  ccDuration: '',
  ccDurationUnit: null,
  secondaryComplaint: '',
  scDuration: '',
  scDurationUnit: null,
  otherComplaints: [],
  alsAssessmentRationale: '',
  alsAssessment: null,
  __areDefaultsSet: false,
}

export const initialHistoryState: IHistoryState = {
  drugalc: null,
  drugalcIndicators: [],
  drugalcIndicatorsPNNV: null,
}

export const initialCcHpiInjuryDetailsState: ICcHpiInjuryDetailsState = {
  intentional: null,
  traumaTriageCriteriaHighRisk: [],
  traumaTriageCriteriaHighRiskPNNV: null,
  traumaTriageCriteriaModerateRisk: [],
  traumaTriageCriteriaModerateRiskPNNV: null,
  injurycause: [],
  mechanism: [],
  mechanismPNNV: null,
  safetyEquipment: [],
  safetyEquipmentPNNV: null,
  fallHeight: '',
  fallsurface: null,
  landedon: null,
  workrelated: null,
  workrelatedPNNV: null,
}

export const initialCcHpiCardiacArrestState: ICcHpiCardiacArrestState = {
  arrestPresent: null,
  arrestPresentPNNV: null,
  dtWitnessed: '',
  dtWitnessedPNNV: null,
  unkWitnessed: 1,
  whoWitnessed: [],
  etiology: null,
  etiologyPNNV: null,
  therapeuticHypothermia: null,
  initialRhythm: null,
  initialRhythmPNNV: null,
  dtCpr: '',
  unkCpr: 1,
  cprPriorEmsArrival: null,
  cprPriorEmsArrivalPNNV: null,
  firstCpr: null,
  firstCprPNNV: null,
  cprBy: [],
  cprType: [],
  resuscitation: [],
  aedUse: null,
  firstAedBy: null,
  firstAedByPNNV: null,
  aedBy: [],
  dtDefib: '',
  unkDefib: 1,
  defibType: null,
  defibBy: null,
  defibByPNNV: null,
  dtAls: '',
  unkAls: 1,
  dtDisc: '',
  unkDisc: 1,
  discReason: null,
  discReasonPNNV: null,
  dtCirculation: '',
  unkCirculation: 1,
  returnCirculation: [],
  dtVent: '',
  unkVent: 1,
  destRhythm: [],
  destRhythmPNNV: null,
  arrestResult: null,
}

export function situationReducer(
  state: ISituationState = initialSituationState,
  action: ICcHpiAction
): ISituationState {
  switch (action.type) {
    case CcHpiConstants.SET_SEGMENT:
      return { ...state, segment: action.segment }
    case CcHpiConstants.SET_CHIEF_COMPLAINT:
      return { ...state, complaint: action.complaint }
    case CcHpiConstants.SET_ALS_ASSESSMENT:
      return { ...state, alsAssessment: action.alsAssessment }
    case CcHpiConstants.SET_ALS_ASSESSMENT_RATIONALE:
      return { ...state, alsAssessmentRationale: action.alsAssessmentRationale }
    case CcHpiConstants.SET_PATIENT_ACTIVITY:
      return { ...state, patientActivity: action.patientActivity }
    case CcHpiConstants.SET_REASON_FOR_ENCOUNTER:
      return { ...state, possibleInjury: action.possibleInjury }
    case CcHpiConstants.SET_CCDURATION:
      return { ...state, ccDuration: action.ccDuration }
    case CcHpiConstants.SET_CCDURATION_UNIT:
      return { ...state, ccDurationUnit: action.ccDurationUnit }
    case CcHpiConstants.SET_SCDURATION:
      return { ...state, scDuration: action.scDuration }
    case CcHpiConstants.SET_SCDURATION_UNIT:
      return { ...state, scDurationUnit: action.scDurationUnit }
    case CcHpiConstants.SET_SECONDARY_COMPLAINT:
      return { ...state, secondaryComplaint: action.secondaryComplaint }
    case CcHpiConstants.SET_OTHER_COMPLAINTS:
      return { ...state, otherComplaints: action.otherComplaints }
    case CcHpiConstants.SET_CCHPI_DEFAULTS:
      return { ...state, __areDefaultsSet: action.__areDefaultsSet }
    case CcHpiConstants.SET_SITUATION_STATE:
      return { ...state, ...action.situation }
    case CcHpiConstants.RESET_SITUATION_STATE:
      return {
        segment: 'CCH',
        complaint: '',
        patientActivity: [],
        possibleInjury: null,
        ccDuration: '',
        ccDurationUnit: null,
        secondaryComplaint: '',
        scDuration: '',
        scDurationUnit: null,
        otherComplaints: [],
        alsAssessmentRationale: '',
        alsAssessment: null,
        __areDefaultsSet: false,
      }
    default:
      return state
  }
}

export function historyReducer(state: IHistoryState = initialHistoryState, action: ICcHpiAction): IHistoryState {
  switch (action.type) {
    case CcHpiConstants.SET_DRUGS_AND_ALCOHOL:
      return { ...state, drugalc: action.drugalc }
    case CcHpiConstants.SET_DRUGS_AND_ALCOHOL_INDICATORS:
      return { ...state, drugalcIndicators: action.drugalcIndicators }
    case CcHpiConstants.SET_DRUGS_AND_ALCOHOL_INDICATORS_PNNV:
      return { ...state, drugalcIndicatorsPNNV: action.drugalcIndicatorsPNNV }
    case CcHpiConstants.SET_HISTORY_STATE:
      return { ...state, ...action.history }
    case CcHpiConstants.RESET_HISTORY_STATE:
      return { ...initialHistoryState }
    default:
      return state
  }
}

export function ccHpiInjuryDetailsReducer(
  state: ICcHpiInjuryDetailsState = initialCcHpiInjuryDetailsState,
  action: ICcHpiAction
): ICcHpiInjuryDetailsState {
  switch (action.type) {
    case CcHpiConstants.SET_INTENTIONAL:
      return { ...state, intentional: action.intentional }
    case CcHpiConstants.SET_TRAUMA_TRIAGE_CRITERIA_HIGH_RISK:
      return { ...state, traumaTriageCriteriaHighRisk: action.traumaTriageCriteriaHighRisk }
    case CcHpiConstants.SET_TRAUMA_TRIAGE_CRITERIA_HIGH_RISK_PNNV:
      return { ...state, traumaTriageCriteriaHighRiskPNNV: action.traumaTriageCriteriaHighRiskPNNV }
    case CcHpiConstants.SET_TRAUMA_TRIAGE_CRITERIA_MODERATE_RISK:
      return { ...state, traumaTriageCriteriaModerateRisk: action.traumaTriageCriteriaModerateRisk }
    case CcHpiConstants.SET_TRAUMA_TRIAGE_CRITERIA_MODERATE_RISK_PNNV:
      return { ...state, traumaTriageCriteriaModerateRiskPNNV: action.traumaTriageCriteriaModerateRiskPNNV }
    case CcHpiConstants.SET_INJURY_CAUSE:
      return { ...state, injurycause: action.injurycause }
    case CcHpiConstants.SET_MECHANISM:
      return { ...state, mechanism: action.mechanism }
    case CcHpiConstants.SET_MECHANISM_PNNV:
      return { ...state, mechanismPNNV: action.mechanismPNNV }
    case CcHpiConstants.SET_SAFETY_EQUIPMENT:
      return { ...state, safetyEquipment: action.safetyEquipment }
    case CcHpiConstants.SET_SAFETY_EQUIPMENT_PNNV:
      return { ...state, safetyEquipmentPNNV: action.safetyEquipmentPNNV }
    case CcHpiConstants.SET_FALL_HEIGHT:
      return { ...state, fallHeight: action.fallHeight }
    case CcHpiConstants.SET_FALL_SURFACE:
      return { ...state, fallsurface: action.fallsurface }
    case CcHpiConstants.SET_LANDED_ON:
      return { ...state, landedon: action.landedon }
    case CcHpiConstants.SET_WORK_RELATED:
      return { ...state, workrelated: action.workrelated }
    case CcHpiConstants.SET_WORK_RELATED_PNNV:
      return { ...state, workrelatedPNNV: action.workrelatedPNNV }
    case CcHpiConstants.SET_CCHPI_INJURY_DETAILS:
      return { ...action.ccHpiInjuryDetails }
    case CcHpiConstants.RESET_CCHPI_INJURY_DETAILS:
      return { ...initialCcHpiInjuryDetailsState }
    default:
      return state
  }
}

export function ccHpiCardiacArrestReducer(
  state: ICcHpiCardiacArrestState = initialCcHpiCardiacArrestState,
  action: ICcHpiAction
): ICcHpiCardiacArrestState {
  switch (action.type) {
    case CcHpiConstants.SET_ARREST_PRESENT:
      return { ...state, arrestPresent: action.arrestPresent }
    case CcHpiConstants.SET_ARREST_PRESENT_PNNV:
      return { ...state, arrestPresentPNNV: action.arrestPresentPNNV }
    case CcHpiConstants.SET_DTWITNESSED:
      return { ...state, dtWitnessed: action.dtWitnessed }
    case CcHpiConstants.SET_DTWITNESSED_PNNV:
      return { ...state, dtWitnessedPNNV: action.dtWitnessedPNNV }
    case CcHpiConstants.SET_UNK_WITNESSED:
      return { ...state, unkWitnessed: action.unkWitnessed }
    case CcHpiConstants.SET_WHO_WITNESSED:
      return { ...state, whoWitnessed: action.whoWitnessed }
    case CcHpiConstants.SET_ETIOLOGY:
      return { ...state, etiology: action.etiology }
    case CcHpiConstants.SET_ETIOLOGY_PNNV:
      return { ...state, etiologyPNNV: action.etiologyPNNV }
    case CcHpiConstants.SET_THERAPEUTIC_HYPOTHERMIA:
      return { ...state, therapeuticHypothermia: action.therapeuticHypothermia }
    case CcHpiConstants.SET_INITIAL_RHYTHM:
      return { ...state, initialRhythm: action.initialRhythm }
    case CcHpiConstants.SET_INITIAL_RHYTHM_PNNV:
      return { ...state, initialRhythmPNNV: action.initialRhythmPNNV }
    case CcHpiConstants.SET_CPR_PRIOR_EMS_ARRIVAL:
      return { ...state, cprPriorEmsArrival: action.cprPriorEmsArrival }
    case CcHpiConstants.SET_CPR_PRIOR_EMS_ARRIVAL_PNNV:
      return { ...state, cprPriorEmsArrivalPNNV: action.cprPriorEmsArrivalPNNV }
    case CcHpiConstants.SET_FIRST_CPR:
      return { ...state, firstCpr: action.firstCpr }
    case CcHpiConstants.SET_FIRST_CPR_PNNV:
      return { ...state, firstCprPNNV: action.firstCprPNNV }
    case CcHpiConstants.SET_DTCPR:
      return { ...state, dtCpr: action.dtCpr }
    case CcHpiConstants.SET_UNK_CPR:
      return { ...state, unkCpr: action.unkCpr }
    case CcHpiConstants.SET_CPRBY:
      return { ...state, cprBy: action.cprBy }
    case CcHpiConstants.SET_CPRTYPE:
      return { ...state, cprType: action.cprType }
    case CcHpiConstants.SET_RESUSCITATION:
      return { ...state, resuscitation: action.resuscitation }
    case CcHpiConstants.SET_AED_USE:
      return { ...state, aedUse: action.aedUse }
    case CcHpiConstants.SET_FIRST_AED_BY:
      return { ...state, firstAedBy: action.firstAedBy }
    case CcHpiConstants.SET_FIRST_AED_BY_PNNV:
      return { ...state, firstAedByPNNV: action.firstAedByPNNV }
    case CcHpiConstants.SET_AED_BY:
      return { ...state, aedBy: action.aedBy }
    case CcHpiConstants.SET_DTDEFIB:
      return { ...state, dtDefib: action.dtDefib }
    case CcHpiConstants.SET_UNK_DEFIB:
      return { ...state, unkDefib: action.unkDefib }
    case CcHpiConstants.SET_DEFIB_TYPE:
      return { ...state, defibType: action.defibType }
    case CcHpiConstants.SET_DEFIB_BY:
      return { ...state, defibBy: action.defibBy }
    case CcHpiConstants.SET_DEFIB_BY_PNNV:
      return { ...state, defibByPNNV: action.defibByPNNV }
    case CcHpiConstants.SET_DTALS:
      return { ...state, dtAls: action.dtAls }
    case CcHpiConstants.SET_UNK_ALS:
      return { ...state, unkAls: action.unkAls }
    case CcHpiConstants.SET_DTDISC:
      return { ...state, dtDisc: action.dtDisc }
    case CcHpiConstants.SET_UNK_DISC:
      return { ...state, unkDisc: action.unkDisc }
    case CcHpiConstants.SET_DISC_REASON:
      return { ...state, discReason: action.discReason }
    case CcHpiConstants.SET_DISC_REASON_PNNV:
      return { ...state, discReasonPNNV: action.discReasonPNNV }
    case CcHpiConstants.SET_DTCIRCULATION:
      return { ...state, dtCirculation: action.dtCirculation }
    case CcHpiConstants.SET_UNK_CIRCUALATION:
      return { ...state, unkCirculation: action.unkCirculation }
    case CcHpiConstants.SET_RETURN_CIRCULATION:
      return { ...state, returnCirculation: action.returnCirculation }
    case CcHpiConstants.SET_DTVENT:
      return { ...state, dtVent: action.dtVent }
    case CcHpiConstants.SET_UNK_VENT:
      return { ...state, unkVent: action.unkVent }
    case CcHpiConstants.SET_DEST_RHYTHM:
      return { ...state, destRhythm: action.destRhythm }
    case CcHpiConstants.SET_DEST_RHYTHM_PNNV:
      return { ...state, destRhythmPNNV: action.destRhythmPNNV }
    case CcHpiConstants.SET_ARREST_RESULT:
      return { ...state, arrestResult: action.arrestResult }
    case CcHpiConstants.SET_CARDIAC_ARREST_STATE:
      return { ...state, ...action.ccHpicardiacArrest }
    case CcHpiConstants.RESET_CARDIAC_ARREST_STATE:
      return { ...initialCcHpiCardiacArrestState }
    default:
      return state
  }
}
